import { memo } from 'react'
import { Image, X, ExternalLink, Check, Circle } from 'lucide-react'
import type { Program } from '../../types/index.js'
import { formatDurationShort } from '../../lib/utils.js'
import { programReadiness, effectiveGenreLabel } from './readiness.js'
import './ProgramsView.css'

/**
 * Aperçu d'une fiche, à droite du tableau.
 *
 * Il répond à « est-ce bien celle-là, et peut-elle partir ? » sans ouvrir le
 * formulaire. Le bloc « Prêt pour l'antenne » y est détaillé point par point :
 * un badge rouge qui ne dit pas *ce qui* manque oblige à ouvrir la fiche pour
 * le découvrir.
 */

const shortDateTime = (iso: string | null | undefined) => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    + ' · ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

interface ProgramPreviewPanelProps {
  program:  Program
  onEdit:   (program: Program) => void
  onClose:  () => void
}

export const ProgramPreviewPanel = memo(function ProgramPreviewPanel({
  program, onEdit, onClose,
}: ProgramPreviewPanelProps) {
  const readiness = programReadiness(program)
  const genre = effectiveGenreLabel(program)
  const duration = program.totalDurationSec ?? program.durationSec

  return (
    <aside className="catalog-preview" aria-label={`Aperçu de ${program.title}`}>
      <div className="catalog-preview__header">
        <span className="catalog-preview__eyebrow">Aperçu</span>
        <div className="catalog-preview__header-actions">
          <button
            type="button"
            className="catalog-icon-action"
            onClick={() => onEdit(program)}
            aria-label="Ouvrir la fiche complète"
            title="Ouvrir la fiche complète"
          >
            <ExternalLink size={14} />
          </button>
          <button
            type="button"
            className="catalog-icon-action"
            onClick={onClose}
            aria-label="Fermer l’aperçu"
            title="Fermer"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="catalog-preview__thumb">
        {program.thumbnailUrl
          ? <img src={program.thumbnailUrl} alt="" />
          : <Image size={28} aria-hidden="true" />}
        <div className="catalog-preview__chips">
          {program.programCategory?.label && (
            <span className="catalog-preview__chip">{program.programCategory.label}</span>
          )}
          {genre && <span className="catalog-preview__chip">{genre}</span>}
        </div>
      </div>

      <div className="catalog-preview__identity">
        <h3 className="catalog-preview__title">{program.title}</h3>
        {/* Un direct se signale avant tout le reste : c'est ce qui décide de
            la façon dont la régie le traite, indépendamment de sa complétude. */}
        {program.isLive
          ? <span className="catalog-badge catalog-badge--live">DIRECT</span>
          : readiness.ready
            ? <span className="catalog-badge catalog-badge--ok">PRÊT</span>
            : <span className="catalog-badge catalog-badge--warn">À COMPLÉTER</span>}
      </div>

      {program.summary && <p className="catalog-preview__summary">{program.summary}</p>}

      <dl className="catalog-preview__facts">
        <Fact label="Identifiant" value={program.programCode || '—'} />
        <Fact label="Série" value={program.series?.title ?? '—'} />
        {program.subGenre && <Fact label="Sous-genre" value={program.subGenre} />}
        <Fact label="Durée" value={formatDurationShort(duration)} />
        {/* `stats.lastAiredAt` n'est chargé que sur la fiche détaillée : la
            liste ne porte que la première diffusion, et inventer un appel par
            ligne pour une date d'aperçu coûterait plus qu'il ne rapporte. */}
        <Fact label="Première diffusion" value={shortDateTime(program.firstAiredAt) ?? '—'} />
      </dl>

      <div className="catalog-preview__readiness">
        <div className="catalog-preview__readiness-head">
          <span>Prêt pour l’antenne</span>
          <span className="catalog-preview__readiness-score">
            {readiness.satisfiedRequired} / {readiness.totalRequired}
          </span>
        </div>
        <ul className="catalog-preview__checks">
          {readiness.checks.map(check => (
            <li
              key={check.id}
              className={`catalog-preview__check${check.satisfied ? ' is-ok' : check.required ? ' is-missing' : ''}`}
            >
              {check.satisfied
                ? <Check size={13} aria-hidden="true" />
                : <Circle size={13} aria-hidden="true" />}
              <span className="catalog-preview__check-copy">
                <span className="catalog-preview__check-label">{check.label}</span>
                {/* Le détail dit ce qui manque, ou la valeur retenue : un point
                    rouge qui ne nomme pas le défaut oblige à ouvrir la fiche
                    pour le découvrir. */}
                {check.detail && <span className="catalog-preview__check-detail">{check.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="tp-button tp-button--info catalog-preview__edit"
        onClick={() => onEdit(program)}
      >
        Modifier la fiche
      </button>
    </aside>
  )
})

const Fact = memo(function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="catalog-preview__fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
})
